import React, {type ComponentType, type ReactNode} from 'react';

import {skipToken} from '@reduxjs/toolkit/query';

import {GenericDocumentActionButtons} from 'app/components/general/GenericDocumentActionButtons';
import {SPPage} from 'app/general/structures/SPPage';
import {getProcedureName} from 'app/utils/fallbacks';
import {isLastVersion} from 'app/version/utils';

import type {
  LeftSidebarElementType,
  LeftSidebarProps,
} from 'app/components/general/DetailPage/LeftSidebar/structure/types';
import type {
  UseListQueryType,
  UseQueryType,
} from 'app/store/sweet-api/api-query';
import type {
  ProcedureHashidSerializedType,
  ProcedureVersionSerializedType,
  ProcessHashidSerializedType,
  ProcessVersionSerializedType,
} from 'sweet-types';

export interface BlankSidebarPageProps<
  ItemType extends ProcessHashidSerializedType | ProcedureHashidSerializedType
> {
  params: {
    hashid: string;
    versionid: string | null;
  };
  children: ReactNode;
  useData: UseQueryType<string, ItemType>;
  useDataVersion: UseQueryType<
    {versionId: string | number},
    ItemType extends ProcessHashidSerializedType
      ? ProcessVersionSerializedType
      : ProcedureVersionSerializedType
  >;
  useDataVersions: UseListQueryType<
    {
      objectId?: number;
    },
    ItemType extends ProcessHashidSerializedType
      ? ProcessVersionSerializedType
      : ProcedureVersionSerializedType
  >;
  SideBarComponent: ComponentType<LeftSidebarProps<ItemType>>;
  CurrentLeftSidebar?: LeftSidebarElementType<ItemType>;
}

export function BlankSidebarPage<
  ItemType extends ProcessHashidSerializedType | ProcedureHashidSerializedType
>({
  useData,
  useDataVersion,
  useDataVersions,
  params,
  children,
  SideBarComponent,
  CurrentLeftSidebar,
}: BlankSidebarPageProps<ItemType>) {
  const {versionid: versionId, hashid: hashId} = params;
  const {data} = useData(hashId, {refetchOnMountOrArgChange: false});
  const {data: dataVersion} = useDataVersion(
    versionId != null ? {versionId} : skipToken,
    {skip: versionId == null, refetchOnMountOrArgChange: false}
  );
  const {data: dataVersions} = useDataVersions(
    {objectId: data?.id},
    {
      skip: data == null,
      refetchOnMountOrArgChange: false,
    }
  );

  const actionButtonsProps = data
    ? {
        contentType: data.content_type,
        document: data,
        canChange: data.can_change,
      }
    : undefined;

  const sidebar = data && dataVersions != null && (
    <SideBarComponent CurrentLeftSidebar={CurrentLeftSidebar} />
  );

  return (
    <SPPage
      pageTitle={data?.name}
      breadcrumbProps={{
        procedureId: data?.id,
        docName: getProcedureName(data?.name),
        link: data?.html_url,
        version: dataVersion,
      }}
      horizontalPadding={false}
      actionArea={
        actionButtonsProps &&
        dataVersions &&
        (dataVersion != null ? (
          <GenericDocumentActionButtons
            version={dataVersion}
            isLastVersion={isLastVersion(dataVersion, dataVersions.results)}
            pageContext={'view-version'}
            {...actionButtonsProps}
          />
        ) : (
          <GenericDocumentActionButtons
            pageContext={'view-published'}
            {...actionButtonsProps}
          />
        ))
      }
      sidebar={sidebar}
    >
      {children}
    </SPPage>
  );
}
